import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  PlusCircle,
  RefreshCw,
  Edit,
  Trash2,
  Mail,
  Phone,
  Briefcase,
  Bell,
  CheckCircle2,
  X,
  Loader2,
  AlertCircle,
  User as UserIcon,
} from 'lucide-react';
import {
  Card,
  CardContent,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useToast } from '../../../components/ui/toaster.jsx';
import {
  fetchClientContacts,
  createClientContact,
  updateClientContact,
  deleteClientContact,
} from '../Services/clientContactsService';
import {
  TIPO_CONTACTO_OPTIONS,
  TIPO_CONTACTO_BADGE,
  TIPO_NOTIFICACION_OPTIONS,
  ESTADO_OPTIONS,
} from '../utils/clientContactsColumns';
import { cn } from '../../../lib/utils';

const ESTADO_BADGE = {
  Activo: 'success',
  Inactivo: 'secondary',
};

const MAX_VISIBLE_NOTIF_BADGES = 3;

export default function ContactsSection({ clienteId, clienteNombre }) {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchClientContacts(clienteId);
      if (!r?.success) {
        toast.error('Error al cargar contactos', r?.message);
        return;
      }
      setData(r.data || []);
    } catch (e) {
      toast.error('Error al cargar contactos', e?.message);
    } finally {
      setLoading(false);
    }
  }, [clienteId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        nombre: formData.nombre.trim(),
        correo: formData.correo.trim().toLowerCase(),
        cargo: formData.cargo?.trim() || undefined,
        telefono: formData.telefono?.trim() || undefined,
        observaciones: formData.observaciones?.trim() || undefined,
      };
      const r = editing
        ? await updateClientContact(clienteId, editing._id, payload)
        : await createClientContact(clienteId, payload);
      if (!r?.success) {
        toast.error(editing ? 'Error al actualizar' : 'Error al crear', r?.message);
        return;
      }
      toast.success(
        editing ? 'Contacto actualizado' : 'Contacto creado',
        payload.nombre,
      );
      closeModal();
      load();
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (c) => {
    setDeleteId(c._id);
    setDeleteName(c.nombre);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const r = await deleteClientContact(clienteId, deleteId);
      if (r?.success) {
        toast.success('Contacto eliminado', deleteName);
      } else {
        toast.error('Error al eliminar', r?.message);
        return;
      }
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setDeleteId(null);
      setDeleteName('');
      load();
    }
  };

  const totalActivos = useMemo(
    () => data.filter((c) => c.estado === 'Activo').length,
    [data],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="space-y-4"
    >
      <Card>
        <CardContent className="p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Contactos</h2>
                <p className="text-sm text-muted-foreground">
                  {data.length === 0
                    ? clienteNombre
                      ? `Sin contactos registrados para ${clienteNombre}.`
                      : 'Sin contactos registrados.'
                    : `${data.length} contacto${data.length === 1 ? '' : 's'} · ${totalActivos} activo${
                        totalActivos === 1 ? '' : 's'
                      }`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                Actualizar
              </Button>
              <Button size="sm" onClick={openCreate}>
                <PlusCircle className="h-4 w-4" />
                Nuevo Contacto
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && data.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
            Cargando contactos…
          </CardContent>
        </Card>
      )}

      {!loading && data.length === 0 && (
        <EmptyContactsState onCreate={openCreate} clienteNombre={clienteNombre} />
      )}

      {data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.map((c) => (
            <ContactCard
              key={c._id}
              contact={c}
              onEdit={() => openEdit(c)}
              onDelete={() => askDelete(c)}
            />
          ))}
        </div>
      )}

      <ContactFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        item={editing}
        clienteNombre={clienteNombre}
        saving={saving}
      />

      <AlertDialog open={Boolean(deleteId)} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar contacto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará a <strong>{deleteName}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}

// ---------- Tarjeta de contacto ----------

function ContactCard({ contact, onEdit, onDelete }) {
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn?.();
  };
  const notifs = Array.isArray(contact.tiposNotificacion)
    ? contact.tiposNotificacion
    : [];
  const visibleNotifs = notifs.slice(0, MAX_VISIBLE_NOTIF_BADGES);
  const hiddenCount = notifs.length - visibleNotifs.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      whileHover={{ y: -2 }}
    >
      <Card className="h-full transition-shadow hover:shadow-md">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold shrink-0">
              {(contact.nombre || '?').trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-foreground truncate" title={contact.nombre}>
                  {contact.nombre}
                </h3>
                <Badge
                  variant={ESTADO_BADGE[contact.estado] || 'secondary'}
                  className="shrink-0"
                >
                  {contact.estado}
                </Badge>
              </div>
              {contact.cargo && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {contact.cargo}
                </p>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {contact.tipoContacto && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full',
                  TIPO_CONTACTO_BADGE[contact.tipoContacto] || 'bg-slate-100 text-slate-800',
                )}
              >
                <Briefcase className="h-3 w-3" />
                {contact.tipoContacto}
              </span>
            )}
            {visibleNotifs.map((n) => (
              <span
                key={n}
                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100"
              >
                <Bell className="h-3 w-3" />
                {n}
              </span>
            ))}
            {hiddenCount > 0 && (
              <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                +{hiddenCount} más
              </span>
            )}
          </div>

          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <a
                href={`mailto:${contact.correo}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate hover:text-foreground"
                title={contact.correo}
              >
                {contact.correo}
              </a>
            </div>
            {contact.telefono && (
              <div className="flex items-center gap-2 min-w-0">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{contact.telefono}</span>
              </div>
            )}
            {contact.observaciones && (
              <div className="flex items-start gap-2 min-w-0 pt-1">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span className="text-xs text-muted-foreground line-clamp-2">
                  {contact.observaciones}
                </span>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-border flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={stop(onEdit)}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <Edit className="h-3.5 w-3.5" />
              Editar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={stop(onDelete)}
              aria-label="Eliminar"
              className="h-8 px-2 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ---------- Empty state ----------

function EmptyContactsState({ onCreate, clienteNombre }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card>
        <CardContent className="py-16 px-6">
          <div className="flex flex-col items-center text-center max-w-md mx-auto space-y-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Users className="h-8 w-8" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-foreground">Sin contactos todavía</h3>
              <p className="text-sm text-muted-foreground">
                {clienteNombre
                  ? `Agrega los contactos de ${clienteNombre}: comercial, facturación, operativo, etc., y elige a quién notificar.`
                  : 'Agrega los contactos del cliente: comercial, facturación, operativo, etc., y elige a quién notificar.'}
              </p>
            </div>
            <Button onClick={onCreate}>
              <PlusCircle className="h-4 w-4" />
              Crear primer contacto
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ---------- Modal de crear/editar ----------

function ContactFormModal({ isOpen, onClose, onSave, item, clienteNombre, saving }) {
  const [formData, setFormData] = useState(() => buildInitial(item));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitial(item));
      setErrors({});
    }
  }, [isOpen, item]);

  const isEdit = Boolean(item);

  const setField = (key, value) => {
    setFormData((p) => ({ ...p, [key]: value }));
    if (errors[key]) {
      setErrors((e) => {
        const n = { ...e };
        delete n[key];
        return n;
      });
    }
  };

  const toggleNotif = (value) => {
    setFormData((p) => {
      const current = Array.isArray(p.tiposNotificacion) ? p.tiposNotificacion : [];
      const exists = current.includes(value);
      // Si marca "Todos", reemplaza todo. Si desmarca "Todos", no hace nada raro.
      if (value === 'Todos') {
        return {
          ...p,
          tiposNotificacion: exists ? [] : ['Todos'],
        };
      }
      // Si está "Todos" activo y marca otro, quitamos "Todos" (semántica: específico gana).
      const withoutTodos = current.filter((v) => v !== 'Todos');
      if (exists) {
        return { ...p, tiposNotificacion: withoutTodos.filter((v) => v !== value) };
      }
      return { ...p, tiposNotificacion: [...withoutTodos, value] };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!formData.nombre?.trim()) errs.nombre = 'Nombre es requerido';
    if (!formData.tipoContacto) errs.tipoContacto = 'Tipo de contacto es requerido';
    if (!formData.correo?.trim()) errs.correo = 'Correo es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.correo.trim())) {
      errs.correo = 'Correo no es válido';
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSave(formData);
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar Contacto' : 'Nuevo Contacto'}
      subtitle={
        clienteNombre
          ? `Para el cliente ${clienteNombre}.`
          : 'Agrega un contacto y define a qué eventos notificarlo.'
      }
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="nombre"
            label="Nombre"
            required
            error={errors.nombre}
            icon={UserIcon}
          >
            <Input
              id="nombre"
              value={formData.nombre}
              onChange={(e) => setField('nombre', e.target.value)}
              placeholder="Nombre completo"
              autoFocus
              className={cn(errors.nombre && 'border-destructive', 'pl-9')}
              autoComplete="off"
            />
          </FormField>
          <FormField id="cargo" label="Cargo" icon={Briefcase} error={errors.cargo}>
            <Input
              id="cargo"
              value={formData.cargo}
              onChange={(e) => setField('cargo', e.target.value)}
              placeholder="Ej. Gerente Comercial"
              className="pl-9"
              autoComplete="off"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="tipoContacto"
            label="Tipo de contacto"
            required
            error={errors.tipoContacto}
          >
            <select
              id="tipoContacto"
              value={formData.tipoContacto}
              onChange={(e) => setField('tipoContacto', e.target.value)}
              className={cn(
                'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                errors.tipoContacto ? 'border-destructive' : 'border-input',
              )}
            >
              <option value="">— Selecciona —</option>
              {TIPO_CONTACTO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField id="estado" label="Estado">
            <select
              id="estado"
              value={formData.estado}
              onChange={(e) => setField('estado', e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {ESTADO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            id="correo"
            label="Correo electrónico"
            required
            error={errors.correo}
            icon={Mail}
          >
            <Input
              id="correo"
              type="email"
              value={formData.correo}
              onChange={(e) => setField('correo', e.target.value)}
              placeholder="contacto@empresa.com"
              className={cn(errors.correo && 'border-destructive', 'pl-9')}
              autoComplete="off"
            />
          </FormField>
          <FormField id="telefono" label="Teléfono" icon={Phone} error={errors.telefono}>
            <Input
              id="telefono"
              value={formData.telefono}
              onChange={(e) => setField('telefono', e.target.value)}
              placeholder="+57 300 000 0000"
              className="pl-9"
              autoComplete="off"
            />
          </FormField>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Tipos de notificación
            </Label>
            <Badge variant="outline" className="font-normal text-[10px] py-0 px-1.5">
              Para más adelante
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Marca a qué eventos el sistema debe enviarle notificaciones a este contacto.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {TIPO_NOTIFICACION_OPTIONS.map((opt) => {
              const checked = (formData.tiposNotificacion || []).includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors',
                    checked
                      ? 'border-primary bg-primary/5'
                      : 'border-input hover:bg-muted/40',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleNotif(opt.value)}
                    className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      {checked ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      {opt.label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {opt.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <Label
            htmlFor="observaciones"
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Observaciones
          </Label>
          <textarea
            id="observaciones"
            rows={3}
            value={formData.observaciones}
            onChange={(e) => setField('observaciones', e.target.value)}
            placeholder="Notas, horarios de atención, preferencias, etc."
            className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="h-4 w-4" />
            )}
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear Contacto'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function buildInitial(item) {
  if (!item) {
    return {
      nombre: '',
      cargo: '',
      tipoContacto: '',
      estado: 'Activo',
      correo: '',
      telefono: '',
      tiposNotificacion: [],
      observaciones: '',
    };
  }
  return {
    nombre: item.nombre || '',
    cargo: item.cargo || '',
    tipoContacto: item.tipoContacto || '',
    estado: item.estado || 'Activo',
    correo: item.correo || '',
    telefono: item.telefono || '',
    tiposNotificacion: Array.isArray(item.tiposNotificacion)
      ? [...item.tiposNotificacion]
      : [],
    observaciones: item.observaciones || '',
  };
}

function FormField({ id, label, required, error, icon: Icon, children }) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={id}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      <div className="relative">
        {Icon && (
          <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        )}
        {children}
      </div>
      {error && <p className="text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}

// Modal genérico (mismo patrón que DynamicFormModal, pero
// con control total sobre el contenido para soportar checkboxes).
function ModalShell({ isOpen, onClose, title, subtitle, maxWidth = 'max-w-lg', children }) {
  if (!isOpen) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className={cn(
          'bg-card p-6 rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto border border-border',
          maxWidth,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Cerrar"
            className="-mt-1 -mr-1"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
